import { useCallback, useEffect, useRef, useState } from 'react'

import { generateNewChatPath } from '@bettermode/common/utils'
import { T } from '@tribeplatform/react-components/i18n'
import { MessagingDropdownLoading } from '@tribeplatform/react-components/LoadingStates'
import type { MessagingDropdownList } from '@tribeplatform/react-components/Messaging'
import { useRouter } from '@tribeplatform/react-sdk'
import {
  CHATS_QUERY,
  useChats,
  useUnreadChatsCount,
} from '@tribeplatform/react-sdk/hooks'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import {
  IndicatorBadge,
  IndicatorBadgeWrapper,
} from '@tribeplatform/react-ui-kit/IndicatorBadge'
import { Popover } from '@tribeplatform/react-ui-kit/Popover'
import { TopBarButton } from '@tribeplatform/react-ui-kit/TopBar'

import { useOpenWhenReady } from '../hooks/useOpenWhenReady.js'

const loadMessaging = () => import('@tribeplatform/react-components/Messaging')

/** Delay before the dropdown code is fetched in the background. */
const CODE_PRELOAD_DELAY = 3000 // ms

interface MessagesButtonProps {
  label: string
}
export const MessagesButton = ({ label }: MessagesButtonProps) => {
  const { Link } = useRouter()
  const [warmedUp, setWarmedUp] = useState(false)
  // Held in state rather than React.lazy: lazy always suspends on its first
  // render, even when the module is already loaded, which flashes the skeleton.
  const [DropdownList, setDropdownList] =
    useState<typeof MessagingDropdownList>()

  const { data: unreadChatsCount } = useUnreadChatsCount()
  const unreadMessagesCount = unreadChatsCount?.active.unreadChatsCount ?? 0

  // Fetched once the button is hovered or focused, so the chats are usually
  // cached by the time it is clicked. Background refetches are left to the
  // dropdown's own observer, so this one does not keep refetching on every page.
  const { isSuccess: chatsReady } = useChats({
    variables: CHATS_QUERY,
    useInfiniteQueryOptions: {
      enabled: warmedUp,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  })

  const activatorRef = useRef<HTMLDivElement>(null)
  const { open, toggle, close } = useOpenWhenReady(
    !!DropdownList && chatsReady,
    activatorRef,
  )

  // A failed chunk load leaves the skeleton in place; the header and footer
  // links still work.
  const loadDropdownList = useCallback(
    () =>
      loadMessaging()
        .then(m => setDropdownList(() => m.MessagingDropdownList))
        .catch(() => undefined),
    [],
  )

  useEffect(() => {
    const id = window.setTimeout(loadDropdownList, CODE_PRELOAD_DELAY)
    return () => window.clearTimeout(id)
  }, [loadDropdownList])

  const warmUp = () => {
    setWarmedUp(true)
    loadDropdownList()
  }

  return (
    <div
      ref={activatorRef}
      className="h-full flex justify-center items-center shrink-0"
    >
      <Popover
        placement="bottom-end"
        open={open}
        onClose={close}
        activator={
          <IndicatorBadgeWrapper
            indicatorBadge={
              <IndicatorBadge count={unreadMessagesCount} size="sm" />
            }
          >
            <TopBarButton
              onClick={() => {
                warmUp()
                toggle()
              }}
              onMouseEnter={warmUp}
              onFocus={warmUp}
              icon={<SvgIcon name="message" />}
              aria-label={label}
            />
          </IndicatorBadgeWrapper>
        }
        fluidContent={false}
        fixed
      >
        <div className="flex flex-col">
          <div className="border-b border-line-subdued px-3 py-2 flex items-center gap-2">
            <Link
              className="flex-grow text-content-subdued font-semibold hover:text-content-hovered"
              href="/messaging"
              onClick={close}
            >
              <T defaultMessage="Messages" id="Generics.Messages" />
            </Link>
            <IconButton
              as={Link}
              href={generateNewChatPath()}
              icon={<SvgIcon name="plus" className="text-content-subdued" />}
              variant="secondaryNeutral"
              size="lg"
              className="shrink-0"
            />
          </div>
          <div className="grow">
            {DropdownList ? (
              <DropdownList closeDropdown={close} />
            ) : (
              <MessagingDropdownLoading />
            )}
          </div>
          <div className="border-t border-line-subdued flex align-middle items-center space-s-3">
            <Link
              className="flex-grow p-3 text-content-subdued text-center text-sm hover:text-content-hovered"
              href="/messaging"
              onClick={close}
            >
              <T
                defaultMessage="See all messages"
                id="Generics.SeeAllMessages"
              />
            </Link>
          </div>
        </div>
      </Popover>
    </div>
  )
}
